/*
 * picture-swap.js — repaint an <img> that lives inside a <picture>.
 *
 * Since the AAS-5490 retina split, nearly every swappable tile image is wrapped
 * in a <picture> carrying a resolution-gated <source>. A <source> OUTRANKS the
 * <img> it wraps, so a swap that writes only src/srcset on the img downloads the
 * new image and never paints it — visible in the network panel, invisible on
 * screen. DPR1 is unaffected because no gated source matches there, which is
 * exactly what makes the fault look machine-specific.
 *
 * `currentSrc` is the field that tells a working swap from a broken one.
 * Watching `src` shows the attribute changing and hides the fault.
 *
 * The sources are UPDATED rather than removed wherever a ladder exists. Removing
 * them repaints correctly but costs the retina split for the rest of the session
 * — every later paint then serves full quality. Updating keeps the gated source
 * gated: it takes the retina ladder, an ungated one takes the standard ladder,
 * and the split survives the swap.
 *
 * Same shape as atomic-shopify's paintSwatchImage (AAS-5554) on purpose — the
 * two themes should not diverge on a fault they both have.
 *
 * Usage:
 *   window.armadaPaintPicture?.(imgOrPicture, { src, srcset, srcsetRetina });
 *
 * Consumers call it OPTIONALLY. If this asset ever fails to arrive the swatch
 * still selects, the link still updates, and the image simply does not repaint —
 * loud and trivially diagnosable. A local fallback that wrote src/srcset direct
 * would silently reintroduce the exact hi-DPI fault this file exists to fix,
 * which is the harder thing to notice.
 */

/* One home for the density question (AAS-5559 review): the MediaQueryList is
 * cached and `.matches` read live, so a window dragged between monitors of
 * different DPR still answers correctly. Three components asked it three ways
 * before, two of them rebuilding the MQL on every click. */
let armadaHiDpiMql;
window.armadaIsHiDpi = function armadaIsHiDpi() {
  if (armadaHiDpiMql === undefined) {
    armadaHiDpiMql = window.matchMedia
      ? window.matchMedia('(min-resolution: 2dppx), (-webkit-min-device-pixel-ratio: 2)')
      : null;
  }
  return !!armadaHiDpiMql?.matches;
};

/**
 * @param {HTMLImageElement|HTMLPictureElement} scope  the img to repaint, or its <picture>
 * @param {{src?: string, srcset?: string, srcsetRetina?: string}} next
 * @returns {HTMLImageElement|null} the img that was painted
 */
window.armadaPaintPicture = function armadaPaintPicture(scope, next) {
  if (!scope || !next) return null;
  const pic = scope.tagName === 'PICTURE' ? scope : scope.closest?.('picture');
  const img = pic ? pic.querySelector('img') : scope;

  /* Presence, not truthiness. A caller that supplies an EMPTY ladder is saying
   * "this colour has no srcset" — which happens for real: aprimo-srcset emits
   * the raw string on line 1 only for a blank or non-Aprimo URL, so a valid src
   * arrives beside an empty srcset. Skipping it (the first cut of this file did)
   * leaves the previous colour's candidates in place on the img AND on every
   * source, and a candidate list outranks src: the attribute changes and
   * `currentSrc` does not. Same bug, narrower path, and broken on DPR1 too. */
  let hasSrcset = 'srcset' in next;
  const hasRetina = 'srcsetRetina' in next;

  /* A new src with no ladder mentioned at all can only mean the same thing —
   * keeping the old ladder would repaint the OLD colour. Treat it as empty
   * rather than as "leave the ladders alone", so the shape cannot be got wrong
   * by a future caller. */
  if (next.src !== undefined && !hasSrcset && !hasRetina) hasSrcset = true;

  if (pic && (hasSrcset || hasRetina)) {
    pic.querySelectorAll('source').forEach((source) => {
      /* A source scoped by resolution is the retina half of the split. One
       * scoped only by width (the mobile art-direction source) is not, and
       * takes the standard ladder like the img does — after a colour swap there
       * is no mobile variant of the new colour, and a source left untouched
       * would keep painting the old one. */
      const gated = /min-resolution|device-pixel-ratio/.test(source.getAttribute('media') || '');
      const value = (gated && next.srcsetRetina) || next.srcset || '';
      /* Empty: remove it. A <source> that MATCHES but offers no candidate
       * resolves to nothing, so leaving it behind is worse than dropping it —
       * the img below can then answer. Nothing is lost that was not already
       * unusable for this colour. */
      if (value) source.srcset = value;
      else source.remove();
    });
  }

  if (!img) return null;
  if (hasSrcset) {
    if (next.srcset) img.srcset = next.srcset;
    else img.removeAttribute('srcset');
  }
  if (next.src) img.src = next.src;
  return img;
};
